import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Html5Qrcode } from "html5-qrcode";
import { CameraOff, Loader2, RotateCcw, Store, TicketPercent, X } from "lucide-react";
import Modal from "@/components/Modal";
import { RedeemCouponAPI } from "@/services/api/coupon/redeemCoupon";
import { BusinessScanToUseRedeemedCouponAPI } from "@/services/api/coupon/useRedeemedCoupons";
import {
  getRedeemMessageKey,
  getScanMessageKey,
  parseRedeemedCouponQrValue,
} from "@/utils/couponFormat";

type CameraError = "permission" | "no-camera" | "busy" | "insecure" | "unknown";

const CAMERA_MESSAGES: Record<CameraError, string> = {
  permission: "Camera permission denied. Please enable it in your browser settings.",
  "no-camera": "No camera found on this device.",
  busy: "The camera is being used by another application.",
  insecure: "Camera access requires a secure (HTTPS) connection.",
  unknown: "Unable to start the camera. Please try again.",
};

const classifyCameraError = (error: unknown): CameraError => {
  const message = error instanceof Error ? error.message : String(error);
  if (/NotAllowedError|Permission denied/i.test(message)) return "permission";
  if (/NotFoundError|Requested device not found|Device not found/i.test(message))
    return "no-camera";
  if (/NotReadableError|Track start/i.test(message)) return "busy";
  if (/secure context|mediaDevices|userMedia/i.test(message)) return "insecure";
  return "unknown";
};

interface StudentQRScannerProps {
  active?: boolean;
  onClose: () => void;
  onScanned?: (data: string) => void;
}

/**
 * Web port of the RN `QRScanScreen` / `ModalCamera` pair: a single scanner
 * shared by both roles.
 *
 * - student: the scanned payload is a coupon id, redeemed straight away.
 * - business: the scanned payload is the customer's redeemed-coupon QR value
 *   (`<redeemed id><separator><coupon id>`), split and marked as used.
 *
 * The camera only runs while `active` and no result is pending, and is always
 * stopped on unmount. `hasScanned` is reset whenever the scanner is opened so a
 * student can scan again after a result.
 */
const StudentQRScanner: React.FC<StudentQRScannerProps> = ({
  active = true,
  onClose,
  onScanned,
}) => {
  const { t } = useTranslation();
  const { loggedIn, user_auth, user } = useSelector((state: any) => state.auth);
  const isBusiness = user_auth === "business" || user?.user_auth === "business";

  const elementId = `student-qr-scanner-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const hasScannedRef = useRef(false);
  const [hasScanned, setHasScanned] = useState(false);
  const [running, setRunning] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [cameraError, setCameraError] = useState<CameraError | null>(null);
  const [alertText, setAlertText] = useState("");

  useEffect(() => {
    if (active) {
      hasScannedRef.current = false;
      setHasScanned(false);
    }
  }, [active]);

  const handleScan = useCallback(
    async (decodedText: string) => {
      if (hasScannedRef.current) return;
      hasScannedRef.current = true;
      setHasScanned(true);

      if (!loggedIn) {
        setAlertText(t("Please login"));
        return;
      }

      if (isBusiness) {
        const parsed = parseRedeemedCouponQrValue(decodedText);
        if (!parsed) {
          setAlertText(t("Invalid QR code"));
          return;
        }
        const resp = await BusinessScanToUseRedeemedCouponAPI(parsed.id, parsed.couponId);
        if (resp?.status === 0) {
          setAlertText(t("Scan To Use The Coupon Successfully"));
          onScanned?.(decodedText);
          return;
        }
        setAlertText(t(getScanMessageKey(resp?.status)));
        return;
      }

      const resp = await RedeemCouponAPI(decodedText);
      if (resp?.status === 0) {
        setAlertText(t("Redeem The Coupon Successfully"));
        onScanned?.(decodedText);
        return;
      }
      setAlertText(t(getRedeemMessageKey(resp?.status)));
    },
    [loggedIn, isBusiness, onScanned, t],
  );

  const handleScanRef = useRef(handleScan);
  useEffect(() => {
    handleScanRef.current = handleScan;
  }, [handleScan]);

  const cameraOn = active && !hasScanned;

  useEffect(() => {
    if (!cameraOn) return;
    let cancelled = false;

    const start = async () => {
      setCameraError(null);
      setRunning(false);
      const scanner = new Html5Qrcode(elementId);
      scannerRef.current = scanner;
      try {
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText: string) => handleScanRef.current(decodedText),
          () => {},
        );
      } catch (err) {
        if (!cancelled) {
          scannerRef.current = null;
          setCameraError(classifyCameraError(err));
        }
        return;
      }
      if (!cancelled) setRunning(true);
    };

    const startPromise = start();

    return () => {
      cancelled = true;
      const scanner = scannerRef.current;
      scannerRef.current = null;
      if (!scanner) return;
      startPromise
        .catch(() => {})
        .then(() => (scanner.isScanning ? scanner.stop() : undefined))
        .catch(() => {});
    };
  }, [cameraOn, elementId, retryToken]);

  const handleRetry = () => {
    setCameraError(null);
    setRetryToken((token) => token + 1);
  };

  const handleScanAgain = () => {
    hasScannedRef.current = false;
    setHasScanned(false);
  };

  return (
    <div className="rounded-2xl bg-white p-4 shadow-lg">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fff3e0]">
            {isBusiness ? (
              <Store size={20} className="text-[#ffa217]" />
            ) : (
              <TicketPercent size={20} className="text-[#ffa217]" />
            )}
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900">
              {isBusiness
                ? t("Business: scan customer coupon")
                : t("User: scan to redeem coupon")}
            </div>
            <div className="mt-0.5 text-xs leading-4 text-gray-500">
              {isBusiness
                ? t("Scan customer coupon QR to use")
                : t("Scan QR to redeem coupon")}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("Close")}
          className="shrink-0 rounded-lg p-1 text-gray-400 transition hover:bg-gray-50 hover:text-gray-600"
        >
          <X size={20} />
        </button>
      </div>

      <div className="relative min-h-[280px] overflow-hidden rounded-xl bg-gray-900">
        <div
          id={elementId}
          className="min-h-[280px] w-full [&_video]:h-full [&_video]:w-full [&_video]:object-cover"
        />

        {cameraOn && !running && !cameraError && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 text-white">
            <Loader2 size={26} className="animate-spin" />
            <span className="text-xs">{t("Starting camera...")}</span>
          </div>
        )}

        {cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-900 px-4 text-center text-white">
            <CameraOff size={28} />
            <span className="text-xs leading-4">{t(CAMERA_MESSAGES[cameraError])}</span>
            <button
              type="button"
              onClick={handleRetry}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#ffa217] px-4 py-2 text-xs font-bold text-white transition hover:opacity-90"
            >
              <RotateCcw size={14} />
              {t("Scan again")}
            </button>
          </div>
        )}

        {hasScanned && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-900/95 px-4 text-center text-white">
            <TicketPercent size={28} />
            <button
              type="button"
              onClick={handleScanAgain}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#ffa217] px-4 py-2 text-xs font-bold text-white transition hover:opacity-90"
            >
              <RotateCcw size={14} />
              {t("Scan again")}
            </button>
          </div>
        )}
      </div>

      <ul className="mt-3 space-y-1.5">
        <li className="flex items-start gap-2 text-xs leading-4 text-gray-500">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#ffa217]" />
          {t("Ask customer to show QR")}
        </li>
        <li className="flex items-start gap-2 text-xs leading-4 text-gray-500">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#ffa217]" />
          {t("After success, coupon will be used")}
        </li>
      </ul>

      <Modal visible={Boolean(alertText)} onClose={() => setAlertText("")}>
        <div className="rounded-2xl bg-white p-5 text-center shadow-lg">
          <p className="mb-4 text-sm font-semibold text-gray-900">{alertText}</p>
          <button
            type="button"
            onClick={() => setAlertText("")}
            className="w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default StudentQRScanner;
